import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, getToken, setToken } from "@/lib/api";
import { track, identify, setUserContext, trackSignUp, trackReturnVisit, EVENTS } from "@/lib/analytics";
import { applyConsent, stopClarityIfRecording } from "@/lib/consent";
import { isInternalUser } from "@/lib/internalTraffic";
import { identifyClarityUser } from "@/lib/clarityEvents";

const AuthContext = createContext(null);

const SID_KEY = "mlcn_session_id";

export function AuthProvider({ children }) {
    // user: null = loading; false = not logged in; object = logged in
    const [user, setUser] = useState(null);

    const newUuid = () =>
        (crypto.randomUUID && crypto.randomUUID()) ||
        ("sid-" + Math.random().toString(36).slice(2) + "-" + Date.now().toString(36));

    /**
     * Resolve which session_id this logged-in user should be using and write it
     * to localStorage. The data model is keyed by session_id, so getting this
     * right is what makes "log out → log back in" preserve a caregiver's intake,
     * profile, behavior log, and Lane memory.
     *
     * Priority:
     *   1. Demo accounts (YC reviewer) → use loadedUser.demo_session_id (shared).
     *   2. Real users with primary_session_id on their record → use that (sticky
     *      across devices and re-logins).
     *   3. Legacy users (no primary_session_id yet): if localStorage already has
     *      a non-demo sid, that's almost certainly their original — bind it to
     *      their user record so it persists. Otherwise generate a fresh one and
     *      bind that.
     */
    const resolveSessionForUser = async (loadedUser) => {
        if (!loadedUser) return;

        if (loadedUser.demo_session_id) {
            localStorage.setItem(SID_KEY, loadedUser.demo_session_id);
            return;
        }

        if (loadedUser.primary_session_id) {
            localStorage.setItem(SID_KEY, loadedUser.primary_session_id);
            return;
        }

        // Legacy migration path — only runs once per user.
        const current = localStorage.getItem(SID_KEY);
        const isUsable = current && current !== "yc-demo-session" && !current.startsWith("demo-");
        const sidToBind = isUsable ? current : newUuid();
        localStorage.setItem(SID_KEY, sidToBind);
        try {
            const r = await api.post("/auth/bind-session", { session_id: sidToBind });
            if (r.data?.primary_session_id) {
                localStorage.setItem(SID_KEY, r.data.primary_session_id);
            }
        } catch {
            // Binding failed → at least the user has *something* in localStorage,
            // so the current session works. Next login will retry.
        }
    };

    // Build the user_context blob we attach to every GA4 event.
    // Stays in localStorage so it survives page reloads without a refetch.
    const syncUserContext = (u) => {
        if (!u) { setUserContext(null); return; }
        setUserContext({
            user_type: u.role === "admin"
                ? "admin"
                : (u.subscription_status === "active" ? "subscriber" : "registered"),
            // Include email + role in the cached context so the
            // internal-traffic filter can detect "@emergent.sh signed in
            // mid-session" without re-fetching /auth/me. Reading this
            // from localStorage is how shouldDropEvent() blocks GA4 +
            // Clarity for teammates even on pages that don't otherwise
            // know who's signed in.
            email: u.email || undefined,
            role: u.role || undefined,
            // Research-panel accounts (Prolific / User Interviews / admin-issued
            // tester codes) get tagged so GA4 can filter them out of
            // real-caregiver reports without losing the research sessions.
            ...(u.tester_code ? { traffic_type: "panel_tester" } : {}),
            country: u.country || undefined,
            caregiver_relationship: u.relationship_to_patient || u.profile?.relationship_to_patient || undefined,
            dementia_stage: u.profile?.dementia_stage
                || u.intake?.estimated_stage
                || undefined,
            subscription_plan: u.subscription_plan || undefined,
        });
        // If this user is internal (admin / @emergent.sh / etc.), make
        // sure any Clarity tag that already booted anonymously is shut
        // down NOW, and re-apply consent so GA4 flips to denied. Without
        // this, a teammate who lands on / first (Clarity boots) and then
        // signs in as admin would have their admin session recorded.
        if (isInternalUser(u)) {
            stopClarityIfRecording();
            applyConsent();
        } else {
            // For non-internal users, identify + tag them on Clarity so
            // the founder can filter sessions by user_role, feedback
            // status, etc. from the Clarity dashboard without watching
            // every recording. See lib/clarityEvents.js for the tag
            // vocabulary the segments rely on.
            try {
                identifyClarityUser(u);
            } catch { /* Clarity may not be loaded yet */ }
        }
    };

    const refresh = useCallback(async () => {
        const t = getToken();
        if (!t) { setUser(false); return; }
        try {
            const r = await api.get("/auth/me");
            // Resolve the session id BEFORE exposing the user — components
            // mount as soon as `user` is set and read getSessionId() from
            // localStorage. Setting user first briefly leaves a stale sid
            // (e.g. from a previous account on this browser) in place, which
            // is how one user could see another user's data.
            await resolveSessionForUser(r.data);
            // Keep the user object referentially stable when nothing
            // changed — effects across the app depend on `user`, and a
            // fresh-but-identical object re-runs them all (worst case:
            // fetch loops that trip the rate limiter).
            setUser((prev) => {
                try {
                    if (prev && JSON.stringify(prev) === JSON.stringify(r.data)) return prev;
                } catch { /* fall through to replace */ }
                return r.data;
            });
            syncUserContext(r.data);
            trackReturnVisit(r.data);
        } catch (err) {
            // ONLY clear the token if the server explicitly rejected it
            // (401 = token genuinely invalid/expired). Network blips, 5xx
            // errors, and timeouts must NOT log the user out — that's what
            // was causing the "I came back to my tab and got logged out"
            // problem when the API has a hiccup or the backend restarts.
            const status = err?.response?.status;
            if (status === 401) {
                setToken(null);
                setUser(false);
                return;
            }
            // Transient failure on first load: don't claim "logged in" yet
            // (we have no user object to show), but keep the token in
            // localStorage so the next refresh can recover. Mid-session
            // we leave the existing user state alone entirely.
            setUser((prev) => (prev ? prev : false));
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const login = async (email, password) => {
        const r = await api.post("/auth/login", { email, password });
        setToken(r.data.token);
        await resolveSessionForUser(r.data.user);
        setUser(r.data.user);
        syncUserContext(r.data.user);
        identify(r.data.user?.id);
        track(EVENTS.LOGIN, { method: "password", role: r.data.user?.role });
        return r.data.user;
    };

    const register = async (payload) => {
        const r = await api.post("/auth/register", payload);
        setToken(r.data.token);
        await resolveSessionForUser(r.data.user);
        setUser(r.data.user);
        syncUserContext(r.data.user);
        // Fires GA4-recommended `sign_up` (marked as Key Event in admin)
        // + legacy `account_created`, idempotent per user.
        trackSignUp(r.data.user, { method: "email" });
        return r.data;
    };

    const logout = () => {
        // Clear the HttpOnly session cookie server-side (fire-and-forget —
        // localStorage below is what the SPA itself keys off).
        api.post("/auth/logout").catch(() => { /* non-blocking */ });
        setToken(null);
        setUser(false);
        setUserContext(null);
        identify(null);
        // Clear the session id so the next account on this browser can never
        // read a stale sid before auth resolves. Safe: every account's sid is
        // stored server-side (primary_session_id) and restored at next login.
        localStorage.removeItem(SID_KEY);
        sessionStorage.removeItem("mlcn_skip_onboarding_redirect");
    };

    // Memoize so consumers don't re-render on every parent render.
    const value = useMemo(
        () => ({ user, login, register, logout, refresh }),
        [user]
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
